import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import { camelCase, snakeCase } from 'lodash';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { normalizeArticleStatusRules, validateArticleStatusRules } from './articleStatusRules.ts';
import ArticleStatusRulesField from './ArticleStatusRulesField.tsx';
import InlineSelect from './InlineSelect.tsx';
import { normalizeMinQuantityRules, validateMinQuantityRules } from './minQuantityRules.ts';
import MinQuantityRulesField from './MinQuantityRulesField.tsx';
import { PercentageSliderField } from './SliderField.tsx';
import ValidationChecksField, { ValidationCheckGroup } from './ValidationChecksField.tsx';
import BooleanField from '../../core/components/Fields/BooleanField.tsx';
import JsonField from '../../core/components/Fields/JsonField.tsx';
import SelectField from '../../core/components/Fields/SelectField.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import TagsField from '../../core/components/Fields/TagsField.tsx';
import FormField from '../../core/components/FormField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import SaveStatus from '../../core/components/SaveStatus.tsx';
import SettingsFormCard from '../../core/components/SettingsFormCard.tsx';
import Switch from '../../core/components/Switch.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import ToggleList from '../../core/components/ToggleList.tsx';
import Tooltip from '../../core/components/Tooltip.tsx';
import { COUNTRY_CODES } from '../../core/utils';
import { convertPythonDateTimeFormat } from '../../core/utils/dates.ts';
import useSectionSaveStatus from '../../core/utils/hooks/useSectionSaveStatus.ts';
import { GROUP_SEPARATOR } from '../pages/Assistance/useDocumentConfig.ts';

const SECTION_FIELDS = {
    localization: ['timezone', 'holidayCalendarCode'],
    automation: ['automationEnabled', 'headerConfidenceThreshold', 'lineItemConfidenceThreshold'],
    userAssignment: [
        'userAssignmentEnabled',
        'userAssignmentSource',
        'userAssignmentExtraDataField',
        'userAssignmentDefaultUserEmail',
    ],
    dataFormatting: ['dateFormat', 'displayStreetNrFirst', 'decimalSeparator', 'decimalPlaces'],
    unitConversion: [
        'conversionStyle',
        'conversionOverrideOriginalFields',
        'bestEffortConversion',
        'partitionSpecificUnitConversion',
    ],
    validation: ['validationChecks', 'minQuantityRules', 'articleStatusRules'],
    keywordsToAssistance: ['keywordsToAssistance'],
    documentTypeSpecific: [
        'variantExtractionExamples',
        'defaultCurrency',
        'receiverCountryCode',
        'enableFacturxExtraction',
        'shouldRemoveFreightCostLineItems',
        'orderFormsAllowed',
        'allowRequestStock',
    ],
    backfilling: ['backfillingEnabled', 'backfillingSupportedFormats'],
    documentFiltering: [
        'isDocumentClassificationEnabled',
        'documentClassificationProductTypes',
        'documentClassificationSensitivity',
    ],
};

type DocumentConfigSection = keyof typeof SECTION_FIELDS;

export enum ValidationCheck {
    IncompleteLineItems = 'incomplete_line_items',
    ForgottenLineItems = 'forgotten_line_items',
    DateOrder = 'date_order',
    ErpLock = 'erp_lock',
    LineItemSum = 'line_item_sum',
    MaxQuantity = 'max_quantity',
    MinQuantity = 'min_quantity',
    PosNumber = 'pos_number',
    RemoveBulkOrderSummary = 'remove_bulk_order_summary',
    RepeatedLineItems = 'repeated_line_items_check',
    TotalAmount = 'total_amount',
    CustomerInternalNote = 'customer_internal_note',
    OrderMatching = 'order_matching',
    OrderMatchingPartialConfirmations = 'order_matching_partial_confirmations',
    OfferMatching = 'offer_matching',
    HasOpenOfferCheck = 'has_open_offer_check',
    MultipleSalesOrgs = 'multiple_sales_orgs',
    ThousandSeparatorQuantity = 'thousand_separator_quantity',
    UniqueDocumentNumber = 'unique_document_number',
    ArticleDescriptionCheck = 'article_description_check',
    ArticleStatus = 'article_status',
    UnitPriceMatchesGrossPrice = 'line_item_unit_price_matches_gross_price',
    AgenticExtractionFailed = 'agentic_extraction_failed',
    AgenticContextFilesTooLarge = 'agentic_context_files_too_large',
}

export enum DocumentTypeName {
    DeliveryNote = 'delivery_note',
    Invoice = 'invoice',
    Offer = 'offer',
    Order = 'order',
    OrderConfirmation = 'order_confirmation',
    RequestForQuotation = 'request_for_quotation',
}

const InfoIcon = withIcon(faCircleInfo);

const VALIDATION_CHECK_GROUPS = {
    lineItems: [
        ValidationCheck.IncompleteLineItems,
        ValidationCheck.ForgottenLineItems,
        ValidationCheck.PosNumber,
        ValidationCheck.RemoveBulkOrderSummary,
    ],
    quantitiesAndPrices: [
        ValidationCheck.LineItemSum,
        ValidationCheck.TotalAmount,
        ValidationCheck.MaxQuantity,
        ValidationCheck.MinQuantity,
        ValidationCheck.ThousandSeparatorQuantity,
        ValidationCheck.UnitPriceMatchesGrossPrice,
    ],
    masterDataAndErp: [
        ValidationCheck.OrderMatching,
        ValidationCheck.OrderMatchingPartialConfirmations,
        ValidationCheck.OfferMatching,
        ValidationCheck.HasOpenOfferCheck,
        ValidationCheck.ArticleStatus,
        ValidationCheck.ArticleDescriptionCheck,
        ValidationCheck.MultipleSalesOrgs,
        ValidationCheck.CustomerInternalNote,
        ValidationCheck.ErpLock,
    ],
    duplicates: [ValidationCheck.RepeatedLineItems, ValidationCheck.UniqueDocumentNumber],
};

const GROUPED_VALIDATION_CHECKS: string[] = Object.values(VALIDATION_CHECK_GROUPS).flat();

// the field a check puts its result on, from outsmart-backend documents/validation; checks that aren't listed act on
// the whole document
const VALIDATION_CHECK_FIELDS: Record<string, string> = {
    [ValidationCheck.LineItemSum]: 'line_items__quantity',
    [ValidationCheck.MaxQuantity]: 'line_items__quantity',
    [ValidationCheck.MinQuantity]: 'line_items__quantity',
    [ValidationCheck.ThousandSeparatorQuantity]: 'line_items__quantity',
    [ValidationCheck.UnitPriceMatchesGrossPrice]: 'line_items__unit_price',
    [ValidationCheck.ArticleStatus]: 'line_items__article_number',
    [ValidationCheck.RepeatedLineItems]: 'line_items__article_number',
    [ValidationCheck.ArticleDescriptionCheck]: 'line_items__article_description',
    [ValidationCheck.MultipleSalesOrgs]: 'line_items__sales_organisation',
    [ValidationCheck.HasOpenOfferCheck]: 'quote_number',
};

const DOCUMENT_NUMBER_FIELDS: Record<string, string> = {
    order: 'order_number',
    invoice: 'invoice_number',
    delivery_note: 'delivery_note_number',
    order_confirmation: 'order_confirmation_number',
    request_for_quotation: 'request_number',
};

const getValidationCheckFieldName = (check: string, documentType: string): string | undefined => {
    if (check === ValidationCheck.TotalAmount) return documentType === 'invoice' ? 'net_amount' : 'total_amount_net';
    if (check === ValidationCheck.UniqueDocumentNumber) return DOCUMENT_NUMBER_FIELDS[documentType];
    return VALIDATION_CHECK_FIELDS[check];
};

const getValidationCheckGroups = (availableValidationChecks: string[], value: string[]): ValidationCheckGroup[] => {
    // partial confirmations is a setting of order matching, not a check of its own
    const isVisible = (check: string) =>
        check !== ValidationCheck.OrderMatchingPartialConfirmations || value.includes(ValidationCheck.OrderMatching);

    return [
        ...Object.entries(VALIDATION_CHECK_GROUPS).map(([name, checks]) => ({
            name,
            checks: checks.filter((check) => availableValidationChecks.includes(check) && isVisible(check)),
        })),
        {
            name: 'other',
            checks: availableValidationChecks.filter((check) => !GROUPED_VALIDATION_CHECKS.includes(check)),
        },
    ].filter(({ checks }) => checks.length > 0);
};

const DocumentTypeList = ({ value, onValueChange, onBlur, availableDocumentTypes = [], currentDocumentType }) => {
    return (
        <ToggleList
            value={value}
            onValueChange={onValueChange}
            onBlur={onBlur}
            items={availableDocumentTypes}
            translationPrefix="configPage.document.documentTypeNames"
            requiredItems={[]}
            disabledItems={currentDocumentType ? [snakeCase(currentDocumentType)] : []}
        />
    );
};

const BackfillingSupportedFormatsList = ({ value, onValueChange, onBlur }) => {
    const backfillingSupportedFormats = ['all', 'pdf', 'excel'];

    return (
        <ToggleList
            value={value}
            onValueChange={onValueChange}
            onBlur={onBlur}
            items={backfillingSupportedFormats}
            translationPrefix="configPage.document.backfillingFormatNames"
            requiredItems={[]}
        />
    );
};

const MAX_DECIMAL_PLACES = 10;
const DECIMAL_PLACES_CHOICES = ['2', '3', '4'];

const isValidDecimalPlaces = (value) =>
    String(value).trim() !== '' &&
    Number.isInteger(Number(value)) &&
    Number(value) >= 0 &&
    Number(value) <= MAX_DECIMAL_PLACES;

const formatAmountSample = (decimalPlaces: number, separator: string) =>
    (567.8912345678).toFixed(decimalPlaces).replace('.', separator);

const normalizeOptionValue = (optionName: string, optionValue: any) => {
    if (optionName === 'decimalPlaces') return optionValue === '' || optionValue == null ? null : Number(optionValue);
    if (optionName === 'minQuantityRules') return normalizeMinQuantityRules(optionValue);
    if (optionName === 'articleStatusRules') return normalizeArticleStatusRules(optionValue);
    return optionValue;
};

const FORM_DEFAULT_VALUES = {
    automationEnabled: false,
    headerConfidenceThreshold: '',
    lineItemConfidenceThreshold: '',
    dateFormat: '',
    displayStreetNrFirst: false,
    decimalSeparator: ',',
    decimalPlaces: '',
    conversionStyle: '',
    conversionOverrideOriginalFields: false,
    bestEffortConversion: false,
    partitionSpecificUnitConversion: false,
    validationChecks: [],
    keywordsToAssistance: [],

    // user assignment
    userAssignmentEnabled: false,
    userAssignmentSource: 'CLIENT_MASTER_DATA',
    userAssignmentExtraDataField: '',
    userAssignmentDefaultUserEmail: '',

    // localization
    timezone: '',
    holidayCalendarCode: '',

    // documentTypeSpecific
    backfillingEnabled: false,
    backfillingSupportedFormats: [],
    variantExtractionExamples: undefined,
    defaultCurrency: 'EUR',
    receiverCountryCode: '',
    enableFacturxExtraction: false,
    shouldRemoveFreightCostLineItems: false,
    orderFormsAllowed: false,
    allowRequestStock: false,
    minQuantityRules: [],
    articleStatusRules: [],

    //document filtering
    isDocumentClassificationEnabled: false,
    documentClassificationSensitivity: 'BALANCED',
    documentClassificationProductTypes: [],
};

export const DocumentConfig = ({ config, onSubmit, validationChecks, documentTypes, currentDocumentType }) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();
    const documentConfig: any = useMemo(() => {
        return config ? Object.fromEntries(config.options.map((option) => [camelCase(option.name), option.value])) : {};
    }, [config]);

    const timezoneOptions = React.useMemo(
        () =>
            config?.timezoneOptions?.map((tz) => ({
                label: tz.replace(/_/g, ' '),
                value: tz.replace(/_/g, ' '),
            })) || [],
        [config?.timezoneOptions]
    );

    const holidayCalendarCodeOptions = React.useMemo(
        () =>
            config?.holidayCalendarCodeOptions?.map((code) => ({
                label: (
                    <span className="whitespace-nowrap">
                        {t(`configPage.document.form.holidayCalendarCodes.${code}`)}
                    </span>
                ),
                value: code,
            })) || [],
        [config?.holidayCalendarCodeOptions]
    );

    const supportsDocumentMatchingAssignment = [
        DocumentTypeName.Order,
        DocumentTypeName.Invoice,
        DocumentTypeName.OrderConfirmation,
    ].includes(snakeCase(currentDocumentType) as DocumentTypeName);

    const userAssignmentSourceOptions = [
        {
            label: t('configPage.document.form.userAssignmentSourceOptions.CLIENT_MASTER_DATA'),
            value: 'CLIENT_MASTER_DATA',
        },
        {
            label: t('configPage.document.form.userAssignmentSourceOptions.DOCUMENT_MATCHING'),
            value: 'DOCUMENT_MATCHING',
            disabled: !supportsDocumentMatchingAssignment,
            description: supportsDocumentMatchingAssignment
                ? undefined
                : t('configPage.document.form.userAssignmentSourceOptions.DOCUMENT_MATCHING_unsupported'),
        },
    ];

    const handleSubmit = async (formData) => {
        try {
            if (formData.isDocumentClassificationEnabled) {
                formData.documentClassificationProductTypes = [
                    ...new Set([...(formData.documentClassificationProductTypes || []), 'OTHER']),
                ];
            }

            await onSubmit({
                options: Object.entries(formData)
                    .filter(([optionName, optionValue]) => {
                        // documentTypeSpecific options
                        if (optionName === 'backfillingEnabled' && !('backfillingEnabled' in documentConfig))
                            return false;

                        if (
                            optionName === 'backfillingSupportedFormats' &&
                            !('backfillingSupportedFormats' in documentConfig)
                        )
                            return [];

                        if (
                            optionName === 'variantExtractionExamples' &&
                            !('variantExtractionExamples' in documentConfig)
                        )
                            return false;

                        if (optionName === 'defaultCurrency' && !('defaultCurrency' in documentConfig)) return false;

                        if (optionName === 'receiverCountryCode' && !('receiverCountryCode' in documentConfig))
                            return false;

                        if (optionName === 'enableFacturxExtraction' && !('enableFacturxExtraction' in documentConfig))
                            return false;

                        if (
                            optionName === 'shouldRemoveFreightCostLineItems' &&
                            !('shouldRemoveFreightCostLineItems' in documentConfig)
                        )
                            return false;

                        if (optionName === 'orderFormsAllowed' && !('orderFormsAllowed' in documentConfig))
                            return false;

                        if (optionName === 'decimalPlaces' && !('decimalPlaces' in documentConfig)) return false;
                        if (optionName === 'allowRequestStock' && !('allowRequestStock' in documentConfig))
                            return false;
                        if (optionName === 'minQuantityRules' && !('minQuantityRules' in documentConfig)) return false;
                        if (optionName === 'articleStatusRules' && !('articleStatusRules' in documentConfig))
                            return false;

                        return true;
                    })
                    .map(([optionName, optionValue]: any) => {
                        return {
                            name: snakeCase(optionName),
                            value: normalizeOptionValue(optionName, optionValue),
                        };
                    }),
            });
            publishToast({ description: t('configPage.document.updateSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('configPage.document.updateError'), status: 'error' });
        }
    };

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        form.reset({
            automationEnabled: documentConfig?.automationEnabled,
            headerConfidenceThreshold: documentConfig?.headerConfidenceThreshold || '',
            lineItemConfidenceThreshold: documentConfig?.lineItemConfidenceThreshold || '',
            dateFormat: documentConfig?.dateFormat || '',
            displayStreetNrFirst: documentConfig?.displayStreetNrFirst || false,
            decimalSeparator: documentConfig?.decimalSeparator || ',',
            decimalPlaces: documentConfig?.decimalPlaces != null ? String(documentConfig.decimalPlaces) : '',
            conversionStyle: documentConfig?.conversionStyle || '',
            conversionOverrideOriginalFields: documentConfig?.conversionOverrideOriginalFields,
            bestEffortConversion: documentConfig?.bestEffortConversion,
            partitionSpecificUnitConversion: documentConfig?.partitionSpecificUnitConversion,
            validationChecks: documentConfig?.validationChecks || [],
            keywordsToAssistance: documentConfig?.keywordsToAssistance || [],

            // user assignment
            userAssignmentEnabled: documentConfig?.userAssignmentEnabled || false,
            userAssignmentSource: documentConfig?.userAssignmentSource || 'CLIENT_MASTER_DATA',
            userAssignmentExtraDataField: documentConfig?.userAssignmentExtraDataField || '',
            userAssignmentDefaultUserEmail: documentConfig?.userAssignmentDefaultUserEmail || '',

            // localization
            timezone: documentConfig?.timezone || '',
            holidayCalendarCode: documentConfig?.holidayCalendarCode || '',

            // documentTypeSpecific
            backfillingEnabled: documentConfig?.backfillingEnabled,
            backfillingSupportedFormats: documentConfig?.backfillingSupportedFormats || [],
            variantExtractionExamples: documentConfig?.variantExtractionExamples,
            defaultCurrency: documentConfig?.defaultCurrency || 'EUR',
            receiverCountryCode: documentConfig?.receiverCountryCode || '',
            enableFacturxExtraction: documentConfig?.enableFacturxExtraction || false,
            shouldRemoveFreightCostLineItems: documentConfig?.shouldRemoveFreightCostLineItems || false,
            orderFormsAllowed: documentConfig?.orderFormsAllowed || false,
            allowRequestStock: documentConfig?.allowRequestStock || false,
            minQuantityRules: documentConfig?.minQuantityRules || [],
            articleStatusRules: documentConfig?.articleStatusRules || [],

            // document filtering
            isDocumentClassificationEnabled: documentConfig?.isDocumentClassificationEnabled || false,
            documentClassificationSensitivity: documentConfig?.documentClassificationSensitivity || 'BALANCED',
            documentClassificationProductTypes: documentConfig?.isDocumentClassificationEnabled
                ? [
                      ...new Set([
                          ...(documentConfig?.documentClassificationProductTypes ||
                              (documentTypes || []).filter((type) => type !== snakeCase(currentDocumentType))),
                          'OTHER',
                      ]),
                  ]
                : ['OTHER'],
        });
    }, [config]);

    const sampleDate = new Date();
    sampleDate.setMonth(0);
    sampleDate.setDate(31);

    const choices = ['%d.%m.%Y', '%d/%m/%Y', '%d-%m-%Y', '%m/%d/%Y'];
    const confidenceLevels = {
        STRICT: 95,
        BALANCED: 90,
        LIGHT: 80,
    };

    const formatSample = (value) => `567${value}89`;
    const decimalSeparator = form.watch('decimalSeparator') || ',';

    const supportsVariantExtractionExamples = 'variantExtractionExamples' in documentConfig;
    const supportsDefaultCurrency = 'defaultCurrency' in documentConfig;
    const supportsReceiverCountryCode = 'receiverCountryCode' in documentConfig;
    const supportsEnableFacturxExtraction = 'enableFacturxExtraction' in documentConfig;
    const supportsShouldRemoveFreightCostLineItems = 'shouldRemoveFreightCostLineItems' in documentConfig;
    const supportsOrderFormsAllowed = 'orderFormsAllowed' in documentConfig;
    const supportsDecimalPlaces = 'decimalPlaces' in documentConfig;
    const supportsAllowRequestStock = 'allowRequestStock' in documentConfig;
    const supportsMinQuantityRules = 'minQuantityRules' in documentConfig;
    const supportsArticleStatusRules = 'articleStatusRules' in documentConfig;

    // the field's label as the channel shows it in assistance: its display name, otherwise the standard label
    const getValidationCheckFieldPath = (check: string) => {
        const fieldName = getValidationCheckFieldName(check, currentDocumentType);
        if (!fieldName) return null;

        const displayName = config?.fields
            ?.find((field) => field.name === fieldName)
            ?.options?.find((option) => option.name === 'display_name')?.value;
        const [groupName, subfieldName] = fieldName.split(GROUP_SEPARATOR);
        const fieldLabel =
            displayName ||
            (subfieldName
                ? t(`assistance:itemsView.fieldNames.${subfieldName}`)
                : t(`assistance:headerView.fieldNames.${groupName}`));

        return t('configPage.document.validationCheckFieldPath', {
            scope: t(`configPage.document.validationCheckFieldScopes.${subfieldName ? 'lineItems' : 'header'}`),
            field: fieldLabel,
        });
    };

    const renderValidationCheckSettings = (check: string) => {
        if (check === ValidationCheck.MinQuantity && supportsMinQuantityRules) {
            return (
                <Controller
                    name="minQuantityRules"
                    control={form.control}
                    rules={{
                        validate: (value) =>
                            validateMinQuantityRules(value) ||
                            t('configPage.document.minQuantityRules.extraDataKeyRequired'),
                    }}
                    render={({ field }) => (
                        <MinQuantityRulesField
                            value={field.value}
                            onValueChange={(value) => field.onChange({ target: { value } })}
                            onBlur={field.onBlur}
                            showErrors={!!formErrors.minQuantityRules}
                        />
                    )}
                />
            );
        }
        if (check === ValidationCheck.ArticleStatus && supportsArticleStatusRules) {
            return (
                <Controller
                    name="articleStatusRules"
                    control={form.control}
                    rules={{
                        validate: (value) =>
                            validateArticleStatusRules(value) ||
                            t('configPage.document.articleStatusRules.statusesRequired'),
                    }}
                    render={({ field }) => (
                        <ArticleStatusRulesField
                            value={field.value}
                            onValueChange={(value) => field.onChange({ target: { value } })}
                            onBlur={field.onBlur}
                            showErrors={!!formErrors.articleStatusRules}
                        />
                    )}
                />
            );
        }
        return null;
    };

    const showDocumentTypeSpecific =
        supportsVariantExtractionExamples ||
        supportsDefaultCurrency ||
        supportsReceiverCountryCode ||
        supportsEnableFacturxExtraction ||
        supportsShouldRemoveFreightCostLineItems ||
        supportsOrderFormsAllowed;

    const showDocumentClassification = form.watch('isDocumentClassificationEnabled');

    const { submitSection, getSaveStatusProps } = useSectionSaveStatus(form, handleSubmit);

    const renderSaveStatus = (section: DocumentConfigSection) => (
        <SaveStatus {...getSaveStatusProps(section, SECTION_FIELDS[section])} />
    );

    return (
        <div className="flex flex-col gap-10 relative">
            <SettingsFormCard
                onSubmit={submitSection('localization')}
                title={t('configPage.document.generalSections.localization')}
            >
                <Controller
                    name="timezone"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.timezone')}
                            error={formErrors.timezone}
                            helpText={t('configPage.document.form.timezoneHelpText')}
                        >
                            <SelectField options={timezoneOptions} required={false} {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="holidayCalendarCode"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.holidayCalendarCode')}
                            error={formErrors.holidayCalendarCode}
                        >
                            <SelectField options={holidayCalendarCodeOptions} required={false} {...field} />
                        </FormField>
                    )}
                />

                {renderSaveStatus('localization')}
            </SettingsFormCard>

            <SettingsFormCard
                onSubmit={submitSection('automation')}
                title={t('configPage.document.generalSections.automation')}
            >
                <Controller
                    name="automationEnabled"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.automationEnabled')}
                            error={formErrors.automationEnabled}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />

                <Controller
                    name="headerConfidenceThreshold"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.headerConfidenceThreshold')}
                            error={formErrors.headerConfidenceThreshold}
                        >
                            <PercentageSliderField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="lineItemConfidenceThreshold"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.lineItemConfidenceThreshold')}
                            error={formErrors.lineItemConfidenceThreshold}
                        >
                            <PercentageSliderField {...field} />
                        </FormField>
                    )}
                />

                {renderSaveStatus('automation')}
            </SettingsFormCard>

            <SettingsFormCard
                onSubmit={submitSection('userAssignment')}
                title={t('configPage.document.generalSections.userAssignment')}
            >
                <Controller
                    name="userAssignmentEnabled"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.userAssignmentEnabled')}
                            error={formErrors.userAssignmentEnabled}
                            helpText={t('configPage.document.form.userAssignmentEnabledHelpText')}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />

                <Controller
                    name="userAssignmentSource"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.userAssignmentSource')}
                            error={formErrors.userAssignmentSource}
                            helpText={t('configPage.document.form.userAssignmentSourceHelpText')}
                        >
                            <SelectField options={userAssignmentSourceOptions} required {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="userAssignmentExtraDataField"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.userAssignmentExtraDataField')}
                            error={formErrors.userAssignmentExtraDataField}
                            helpText={t('configPage.document.form.userAssignmentExtraDataFieldHelpText')}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="userAssignmentDefaultUserEmail"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.userAssignmentDefaultUserEmail')}
                            error={formErrors.userAssignmentDefaultUserEmail}
                            helpText={t('configPage.document.form.userAssignmentDefaultUserEmailHelpText')}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                {renderSaveStatus('userAssignment')}
            </SettingsFormCard>

            <SettingsFormCard
                onSubmit={submitSection('dataFormatting')}
                title={t('configPage.document.generalSections.dataFormatting')}
            >
                <Controller
                    name="dateFormat"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('configPage.document.form.dateFormat')} error={formErrors.dateFormat}>
                            <InlineSelect
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                                allowCustomValue={true}
                                customSample={(value) =>
                                    value &&
                                    !choices.includes(value) &&
                                    format(sampleDate, convertPythonDateTimeFormat(value))
                                }
                                options={choices.map((choice) => ({
                                    label: choice,
                                    value: choice,
                                    sample: format(sampleDate, convertPythonDateTimeFormat(choice)),
                                }))}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="displayStreetNrFirst"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.displayStreetNrFirst')}
                            error={formErrors.displayStreetNrFirst}
                        >
                            <InlineSelect
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                                allowCustomValue={false}
                                options={[
                                    {
                                        label: t('configPage.document.addressFormats.streetNameFirst'),
                                        value: false,
                                        sample: 'Main Street 123',
                                    },
                                    {
                                        label: t('configPage.document.addressFormats.streetNrFirst'),
                                        value: true,
                                        sample: '123 Main Street',
                                    },
                                ]}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="decimalSeparator"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.decimalSeparator')}
                            error={formErrors.decimalSeparator}
                        >
                            <InlineSelect
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                                allowCustomValue={false}
                                customSample={(value) => value && !['.', ','].includes(value) && formatSample(value)}
                                options={[
                                    {
                                        label: t('configPage.document.separators.comma'),
                                        value: ',',
                                        sample: formatSample(','),
                                    },
                                    {
                                        label: t('configPage.document.separators.dot'),
                                        value: '.',
                                        sample: formatSample('.'),
                                    },
                                ]}
                            />
                        </FormField>
                    )}
                />

                {supportsDecimalPlaces && (
                    <Controller
                        name="decimalPlaces"
                        control={form.control}
                        rules={{
                            validate: (value) =>
                                value === '' ||
                                isValidDecimalPlaces(value) ||
                                t('configPage.document.form.decimalPlacesInvalid'),
                        }}
                        render={({ field }) => (
                            <FormField
                                label={t('configPage.document.form.decimalPlaces')}
                                error={formErrors.decimalPlaces}
                                helpText={t('configPage.document.form.decimalPlacesHelpText')}
                            >
                                <InlineSelect
                                    value={field.value}
                                    onValueChange={(value) => field.onChange({ target: { value } })}
                                    onBlur={field.onBlur}
                                    allowCustomValue
                                    customSample={(value) =>
                                        value &&
                                        !DECIMAL_PLACES_CHOICES.includes(value) &&
                                        isValidDecimalPlaces(value) &&
                                        formatAmountSample(Number(value), decimalSeparator)
                                    }
                                    options={[
                                        {
                                            label: t('configPage.document.form.decimalPlacesOptions.default'),
                                            value: '',
                                            sample: formatSample(decimalSeparator),
                                        },
                                        ...DECIMAL_PLACES_CHOICES.map((choice) => ({
                                            label: t('configPage.document.form.decimalPlacesOptions.count', {
                                                count: Number(choice),
                                            }),
                                            value: choice,
                                            sample: formatAmountSample(Number(choice), decimalSeparator),
                                        })),
                                    ]}
                                />
                            </FormField>
                        )}
                    />
                )}

                {renderSaveStatus('dataFormatting')}
            </SettingsFormCard>

            <SettingsFormCard
                onSubmit={submitSection('unitConversion')}
                title={t('configPage.document.generalSections.unitConversion')}
            >
                <Controller
                    name="conversionStyle"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.conversionStyle')}
                            error={formErrors.conversionStyle}
                        >
                            <InlineSelect
                                value={field.value}
                                onValueChange={(value) => field.onChange({ target: { value } })}
                                allowCustomValue={true}
                                options={[
                                    // TODO: should this be public?
                                    {
                                        label: t('configPage.document.conversionStyles.NO_CONVERSION'),
                                        value: 'NO_CONVERSION',
                                    },
                                    { label: t('configPage.document.conversionStyles.ROUND_UP'), value: 'ROUND_UP' },
                                    {
                                        label: t('configPage.document.conversionStyles.THIRTY_PERCENT_ROUND'),
                                        value: 'THIRTY_PERCENT_ROUND',
                                    },
                                ]}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="conversionOverrideOriginalFields"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.conversionOverrideOriginalFields')}
                            error={formErrors.conversionOverrideOriginalFields}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />

                <Controller
                    name="bestEffortConversion"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.bestEffortConversion')}
                            error={formErrors.bestEffortConversion}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />

                <Controller
                    name="partitionSpecificUnitConversion"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.document.form.partitionSpecificUnitConversion')}
                            error={formErrors.partitionSpecificUnitConversion}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />

                {renderSaveStatus('unitConversion')}
            </SettingsFormCard>

            <SettingsFormCard
                onSubmit={submitSection('validation')}
                title={t('configPage.document.generalSections.validation')}
                description={t('configPage.document.validationDescription')}
            >
                {validationChecks && (
                    <Controller
                        name="validationChecks"
                        control={form.control}
                        render={({ field }) => (
                            <FormField error={formErrors.validationChecks}>
                                <ValidationChecksField
                                    value={field.value || []}
                                    groups={getValidationCheckGroups(validationChecks, field.value || [])}
                                    onValueChange={(value) => field.onChange({ target: { value } })}
                                    onBlur={field.onBlur}
                                    renderSettings={renderValidationCheckSettings}
                                    getCheckFieldPath={getValidationCheckFieldPath}
                                />
                            </FormField>
                        )}
                    />
                )}

                {renderSaveStatus('validation')}
            </SettingsFormCard>

            <SettingsFormCard
                onSubmit={submitSection('keywordsToAssistance')}
                title={t('configPage.document.form.keywordsToAssistance')}
                description={t('configPage.document.form.keywordsToAssistanceHelpText')}
            >
                <Controller
                    name="keywordsToAssistance"
                    control={form.control}
                    render={({ field }) => (
                        <FormField error={formErrors.keywordsToAssistance}>
                            <TagsField {...field} onValueChange={field.onChange} />
                        </FormField>
                    )}
                />

                {renderSaveStatus('keywordsToAssistance')}
            </SettingsFormCard>

            {showDocumentTypeSpecific && (
                <SettingsFormCard
                    onSubmit={submitSection('documentTypeSpecific')}
                    title={t('configPage.document.generalSections.documentTypeSpecific')}
                >
                    {supportsVariantExtractionExamples && (
                        <Controller
                            name="variantExtractionExamples"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('configPage.document.form.variantExtractionExamples')}
                                    error={formErrors.variantExtractionExamples}
                                >
                                    <JsonField {...field} />
                                </FormField>
                            )}
                        />
                    )}

                    {supportsDefaultCurrency && (
                        <Controller
                            name="defaultCurrency"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('configPage.document.form.defaultCurrency')}
                                    error={formErrors.defaultCurrency}
                                >
                                    <InlineSelect
                                        value={field.value}
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                        allowCustomValue={true}
                                        options={[
                                            { label: 'EUR', value: 'EUR' },
                                            { label: 'USD', value: 'USD' },
                                        ]}
                                    />
                                </FormField>
                            )}
                        />
                    )}

                    {supportsReceiverCountryCode && (
                        <Controller
                            name="receiverCountryCode"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('configPage.document.form.receiverCountryCode')}
                                    error={formErrors.receiverCountryCode}
                                >
                                    <SelectField
                                        options={COUNTRY_CODES.map((code) => ({ label: code, value: code }))}
                                        required={false}
                                        {...field}
                                    />
                                </FormField>
                            )}
                        />
                    )}

                    {supportsEnableFacturxExtraction && (
                        <Controller
                            name="enableFacturxExtraction"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('configPage.document.form.enableFacturxExtraction')}
                                    error={formErrors.enableFacturxExtraction}
                                >
                                    <BooleanField {...field} value={field.value ? 'true' : ''} />
                                </FormField>
                            )}
                        />
                    )}

                    {supportsShouldRemoveFreightCostLineItems && (
                        <Controller
                            name="shouldRemoveFreightCostLineItems"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('configPage.document.form.shouldRemoveFreightCostLineItems')}
                                    error={formErrors.shouldRemoveFreightCostLineItems}
                                >
                                    <BooleanField {...field} value={field.value ? 'true' : ''} />
                                </FormField>
                            )}
                        />
                    )}

                    {supportsOrderFormsAllowed && (
                        <Controller
                            name="orderFormsAllowed"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('configPage.document.form.orderFormsAllowed')}
                                    error={formErrors.orderFormsAllowed}
                                >
                                    <BooleanField {...field} value={field.value ? 'true' : ''} />
                                </FormField>
                            )}
                        />
                    )}

                    {supportsAllowRequestStock && (
                        <Controller
                            name="allowRequestStock"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('configPage.document.form.allowRequestStock')}
                                    error={formErrors.allowRequestStock}
                                >
                                    <BooleanField {...field} value={field.value ? 'true' : ''} />
                                </FormField>
                            )}
                        />
                    )}

                    {renderSaveStatus('documentTypeSpecific')}
                </SettingsFormCard>
            )}

            {'backfillingEnabled' in documentConfig && (
                <SettingsFormCard
                    onSubmit={submitSection('backfilling')}
                    title={t('configPage.document.generalSections.backfilling')}
                >
                    <Controller
                        name="backfillingEnabled"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('configPage.document.form.backfillingEnabled')}
                                error={formErrors.backfillingEnabled}
                            >
                                <BooleanField {...field} value={field.value ? 'true' : ''} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="backfillingSupportedFormats"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('configPage.document.form.backfillingSupportedFormats')}
                                error={formErrors.backfillingSupportedFormats}
                            >
                                <BackfillingSupportedFormatsList
                                    value={field.value}
                                    onValueChange={(value) => field.onChange({ target: { value } })}
                                    onBlur={field.onBlur}
                                />
                            </FormField>
                        )}
                    />

                    {renderSaveStatus('backfilling')}
                </SettingsFormCard>
            )}

            <SettingsFormCard
                onSubmit={submitSection('documentFiltering')}
                title={t('configPage.document.generalSections.documentFiltering')}
                description={t('configPage.document.form.documentFilteringDescription')}
                controls={
                    <Switch
                        value={showDocumentClassification}
                        onValueChange={(value) => {
                            form.setValue('isDocumentClassificationEnabled', value, { shouldDirty: true });
                            if (value) {
                                const types = (documentTypes || []).filter(
                                    (type) => type !== snakeCase(currentDocumentType)
                                );
                                form.setValue('documentClassificationProductTypes', [...new Set([...types, 'OTHER'])], {
                                    shouldDirty: true,
                                });
                            } else {
                                form.setValue('documentClassificationProductTypes', ['OTHER'], { shouldDirty: true });
                                form.setValue('documentClassificationSensitivity', 'BALANCED', { shouldDirty: true });
                            }
                        }}
                    />
                }
                className="gap-0"
            >
                <div className="mt-6 flex flex-col gap-6">
                    {showDocumentClassification && (
                        <>
                            <Controller
                                name="documentClassificationProductTypes"
                                control={form.control}
                                render={({ field }) => (
                                    <FormField
                                        label={t('configPage.document.form.documentFilteringSortOut')}
                                        error={formErrors.validationChecks}
                                    >
                                        <DocumentTypeList
                                            value={field.value}
                                            availableDocumentTypes={documentTypes}
                                            currentDocumentType={currentDocumentType}
                                            onValueChange={(value) => field.onChange({ target: { value } })}
                                            onBlur={field.onBlur}
                                        />
                                    </FormField>
                                )}
                            />
                            <Controller
                                name="documentClassificationSensitivity"
                                control={form.control}
                                render={({ field }) => (
                                    <div className="flex flex-col gap-2">
                                        <div className="flex items-center gap-2 mb-2">
                                            <label className="text-xs text-content-medium flex-none flex justify-start gap-4">
                                                <span className="truncate">
                                                    {t('configPage.document.form.documentFilteringSensitivity')}
                                                </span>
                                            </label>
                                            <Tooltip
                                                className="text-center"
                                                content={t('configPage.document.form.documentFilteringTooltip')}
                                                side="top"
                                            >
                                                <InfoIcon className="w-3 h-3 text-content-medium cursor-pointer" />
                                            </Tooltip>
                                        </div>
                                        <div className="flex gap-1 flex-1 min-w-0 flex-col">
                                            <InlineSelect
                                                value={field.value}
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                                allowCustomValue={false}
                                                options={Object.entries(confidenceLevels).map(([key, value]) => ({
                                                    label: t(
                                                        `configPage.document.filteringSensitivityOptions.${key.toLowerCase()}`
                                                    ),
                                                    value: key,
                                                    sample: `${value}% ${t('configPage.document.filteringSensitivityOptions.confidence')}`,
                                                }))}
                                            />
                                        </div>
                                        {formErrors.conversionStyle?.message && (
                                            <span className="text-content-danger-weak text-xs">
                                                {formErrors.conversionStyle.message}
                                            </span>
                                        )}
                                    </div>
                                )}
                            />
                        </>
                    )}

                    {renderSaveStatus('documentFiltering')}
                </div>
            </SettingsFormCard>
        </div>
    );
};
